import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import {
  BookingRecord,
  BookingStatusEnum,
  countryName,
} from '@pulpo/pulpo-models';
import {
  BookingRecordService,
  CommissionCancellationService,
  HewagoService,
} from '@pulpo/pulpo-api';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonLoadingDirective } from '../../../directives';
import { MatInputModule } from '@angular/material/input';
import { BookingRecordFlightComponent } from './booking-record-flight/booking-record-flight.component';
import { BookingRecordTravelerComponent } from './booking-record-traveler/booking-record-traveler.component';
import { BookingRecordAccommodationComponent } from './booking-record-accommodation/booking-record-accommodation.component';
import { BookingRecordInsuranceComponent } from './booking-record-insurance/booking-record-insurance.component';
import { BookingRecordCarRentalComponent } from './booking-record-car-rental/booking-record-car-rental.component';
import { BookingRecordPaymentComponent } from './booking-record-payment/booking-record-payment.component';
import { BookingRecordOptionComponent } from './booking-record-option/booking-record-option.component';
import dayjs from 'dayjs';
import { BookingRecordBillComponent } from './booking-record-bill/booking-record-bill.component';
import { BookingRecordErrorComponent } from './booking-record-error/booking-record-error.component';
import { PulpoSnackbarComponent } from '../../pulpo-snackbar/pulpo-snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { XmlFormatPipe } from '../../../pipes';

@Component({
  selector: 'booking-record-details',
  templateUrl: './booking-record-details.component.html',
  styleUrls: ['./booking-record-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonToggleModule,
    MatCardModule,
    MatButtonModule,
    MatDividerModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatTabsModule,
    MatTooltipModule,
    MatMenuModule,
    MatButtonLoadingDirective,
    MatInputModule,
    MatDialogModule,
    BookingRecordFlightComponent,
    BookingRecordTravelerComponent,
    BookingRecordAccommodationComponent,
    BookingRecordInsuranceComponent,
    BookingRecordCarRentalComponent,
    BookingRecordPaymentComponent,
    BookingRecordOptionComponent,
    BookingRecordBillComponent,
    BookingRecordErrorComponent,
    CodemirrorModule,
    XmlFormatPipe,
    FormsModule,
  ],
})
export class BookingRecordDetailsComponent implements OnInit {
  bookingRecord: BookingRecord;
  editForm: FormGroup;
  BookingStatusEnum = BookingStatusEnum;
  countryName = countryName;
  dayjs = dayjs;
  isLoading = false;
  hewagoUrls: string[] = [];
  isCurrentlySupplier: boolean;
  producerId: number;
  options = {
    lineNumbers: false,
    theme: 'material',
    mode: 'application/xml',
    readOnly: true,
    lineWrapping: true,
    foldGutter: true,
    gutters: [
      'CodeMirror-linenumbers',
      'CodeMirror-foldgutter',
      'CodeMirror-lint-markers',
    ],
    autoCloseBrackets: true,
    matchBrackets: true,
    background: '#f5f5f5',
  };

  constructor(
    protected activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private bookingRecordService: BookingRecordService,
    private readonly hewagoService: HewagoService,
    protected commissionCancellationService: CommissionCancellationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.activatedRoute.data.subscribe(({ bookingRecord }) => {
      this.bookingRecord = bookingRecord as BookingRecord;
      this.editForm = this.fb.group({
        product: [this.bookingRecord.productRecord?.productName],
        productId: [this.bookingRecord.productRecord?.productId],
        destination: [this.bookingRecord.productRecord?.destinationName],
        distribution: [this.bookingRecord.distributionRecord.distribution],
        producer: [this.bookingRecord.distributionRecord.producerCompanyName],
        supplier: [this.bookingRecord.distributionRecord.supplierCompanyName],
        supplierRef: [this.bookingRecord.externalReference],
        language: [this.bookingRecord.language ? this.bookingRecord.language.toUpperCase() : ''],
        agency: [this.bookingRecord.distributionRecord.agencyInfo?.codeAgency || ''],
      });
      this.isCurrentlySupplier =
        Number(bookingRecord.distributionRecord.supplierId) ===
        Number(bookingRecord.distributionRecord.producerCompanyId);
      this.producerId = this.activatedRoute.snapshot.params['producerId'];

      if (!this.bookingRecord.opened && this.bookingRecord.businessId) {
        this.bookingRecord.openTimeRecord = dayjs();
        this.onConsultChange(true);
      }
      if (this.bookingRecord.businessId) {
        this.hewagoService
          .getDetailLinks(this.bookingRecord.businessId)
          .subscribe((urls) => {
            this.hewagoUrls = urls;
            this.cdr.markForCheck();
          });
      }
      this.cdr.markForCheck();
    });
  }

  extractBalenoRef(url: string): string {
    return /record\/([^/]+)\/show/.exec(url)?.[1] ?? url;
  }

  openHewagoDetail(url?: string) {
    const target =
      url ?? (this.hewagoUrls.length === 1 ? this.hewagoUrls[0] : undefined);
    if (target) {
      window.open(target, '_blank', 'noopener,noreferrer');
    }
  }

  generatePdf(bookingRecord: BookingRecord) {
    this.isLoading = true;
    this.bookingRecordService.generatePdf(bookingRecord.businessId).subscribe(
      (data: any) => {
        const blob = new Blob([data], { type: 'application/pdf' });

        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = 'Res_' + bookingRecord.id + '.pdf';
        link.click();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    );
  }

  onConsultChange(value: boolean) {
    this.bookingRecord.opened = value;
    this.bookingRecordService.update(this.bookingRecord).subscribe((res) => {
      if (res) {
        this.snackBar.openFromComponent(PulpoSnackbarComponent, {
          data: {
            title: 'Modification',
            message:
              'Vous venez de changer le statut de consultation de cette réservation',
          },
          duration: 2000,
          panelClass: ['snackbar'],
        });
        this.bookingRecord = res.body as BookingRecord;
        this.cdr.markForCheck();
      }
    });
  }

  get effectiveFlightRecord() {
    return this.bookingRecord.flightRecord ?? this.bookingRecord.archivedQuoteData?.quotedFlight;
  }

  get effectiveAccommodationRecord() {
    return this.bookingRecord.accommodationRecord ?? this.bookingRecord.archivedQuoteData?.quotedAccommodation;
  }

  get effectiveInsuranceRecord() {
    return this.bookingRecord.insuranceRecord ?? this.bookingRecord.archivedQuoteData?.quotedInsurance;
  }

  get effectiveOptionRecord() {
    return this.bookingRecord.optionRecord ?? this.bookingRecord.archivedQuoteData?.quotedOptions;
  }

  get effectiveBookingPrice() {
    const bp = this.bookingRecord.bookingPrice;
    const qp = this.bookingRecord.archivedQuoteData?.quotedPrice;
    if (!qp) return bp;
    const pref = (qpVal: number | undefined | null, bpVal: number | undefined | null) =>
      qpVal || bpVal || 0;
    return {
      ...bp,
      referenceFlightPrice: pref(qp.referenceFlightPrice, bp.referenceFlightPrice),
      flightPrice: pref(qp.flightPrice, bp.flightPrice),
      totalFlightPrice: pref(qp.totalFlightPrice, bp.totalFlightPrice),
      untaxedFlightPackage: pref(qp.untaxedFlightPackage, bp.untaxedFlightPackage),
      taxedFlightPackage: pref(qp.taxedFlightPackage, bp.taxedFlightPackage),
      taxPrice: pref(qp.taxPrice, bp.taxPrice),
      luggagePrice: pref(qp.luggagePrice, bp.luggagePrice),
      stayPrice: pref(qp.stayPrice, bp.stayPrice),
      packagePrice: pref(qp.packagePrice, bp.packagePrice),
      packageCommission: pref(qp.packageCommission, bp.packageCommission),
      insurancePrice: pref(qp.insurancePrice, bp.insurancePrice),
      totalOptionPrice: pref(qp.totalOptionPrice, bp.totalOptionPrice),
      vehiclePrice: pref(qp.vehiclePrice, bp.vehiclePrice),
      vehicleSupplementPrice: pref(qp.vehicleSupplementPrice, bp.vehicleSupplementPrice),
      vehiclePaidPrice: pref(qp.vehiclePaidPrice, bp.vehiclePaidPrice),
      totalPrice: pref(qp.totalPrice, bp.totalPrice),
      totalCommission: pref(qp.totalCommission, bp.totalCommission),
      commissionPercent: pref(qp.commissionPercent, bp.commissionPercent),
      totalInsuranceCommission: pref(qp.totalInsuranceCommission, bp.totalInsuranceCommission),
      totalLuggageCommission: pref(qp.totalLuggageCommission, bp.totalLuggageCommission),
      totalVehicleCommission: pref(qp.totalVehicleCommission, bp.totalVehicleCommission),
      toBePaidPrice: pref(qp.toBePaidPrice, bp.toBePaidPrice),
      segmentCommissions: qp.segmentCommissions?.length ? qp.segmentCommissions : (bp.segmentCommissions ?? []),
    };
  }

  get startDelay() {
    if (this.bookingRecord.flightRecord?.offer) {
      const wayTrip = this.bookingRecord.flightRecord.offer.way?.segments || [];
      const wayArrival = dayjs(wayTrip[wayTrip.length - 1].arrivalTime);
      let startDate = dayjs(this.bookingRecord.startDate, 'DD/MM/YYYY');
      if (!startDate.isValid()) {
        startDate = dayjs(this.bookingRecord.startDate, 'YYYY-MM-DD');
      }
      if (startDate.date() !== wayArrival.date() && wayArrival.hour() > 6) {
        return '  ( Arrivée +1 )';
      }
    }
    return '';
  }

  resendBookingConfirmation() {
    this.isLoading = true;
    if (this.bookingRecord && this.bookingRecord.businessId) {
      this.bookingRecordService
        .resendConfirmation(this.bookingRecord.businessId)
        .subscribe(
          (res: any) => {
            this.isLoading = false;
            if (res.body) {
              this.snackBar.openFromComponent(PulpoSnackbarComponent, {
                data: {
                  title: 'Message envoyé',
                  message: 'Les mails de confirmation ont bien été renvoyés',
                },
                duration: 4000,
                panelClass: ['snackbar'],
              });
            }
            this.cdr.markForCheck();
          },
          () => {
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        );
    }
  }
}
